import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';

export const NeuralNetwork = () => {
  const svgRef = useRef(null);
  const { t } = useLanguage();
  const lang = t('neuralNetwork');

  // Input data sources (left side)
  const inputNodes = [
    { id: 1, x: 130, y: 100, label: lang.inputs.database },
    { id: 2, x: 130, y: 180, label: lang.inputs.apis },
    { id: 3, x: 130, y: 260, label: lang.inputs.crm },
    { id: 4, x: 130, y: 340, label: lang.inputs.documents },
    { id: 5, x: 130, y: 420, label: lang.inputs.cloud },
    { id: 6, x: 130, y: 500, label: lang.inputs.erp },
  ];

  // Central Doora node
  const centralNode = { id: 'doora', x: 350, y: 300 };

  // Output applications (right side)
  const outputNodes = [
    { id: 7, x: 570, y: 100, label: lang.outputs.copilot },
    { id: 8, x: 570, y: 180, label: lang.outputs.agent },
    { id: 9, x: 570, y: 260, label: lang.outputs.search },
    { id: 10, x: 570, y: 340, label: lang.outputs.analytics },
    { id: 11, x: 570, y: 420, label: lang.outputs.apps },
    { id: 12, x: 570, y: 500, label: lang.outputs.api },
  ];

  // Generate curved path between two points
  const generateCurvedPath = (from, to, curvature = 0.3) => {
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const controlX = midX - dy * curvature;
    const controlY = midY + dx * curvature;
    return `M ${from.x} ${from.y} Q ${controlX} ${controlY} ${to.x} ${to.y}`;
  };

  // Animation variants for edges
  const edgeVariants = {
    hidden: { 
      pathLength: 0, 
      opacity: 0 
    },
    visible: (i) => ({
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { 
          duration: 1.5, 
          delay: i * 0.15, 
          ease: [0.4, 0, 0.2, 1] 
        },
        opacity: { 
          duration: 0.5, 
          delay: i * 0.15 
        },
      },
    }),
  };

  // Pulse animation for central node
  const centralPulseVariants = {
    initial: { scale: 1, opacity: 0.3 },
    animate: {
      scale: [1, 1.8, 1],
      opacity: [0.3, 0, 0.3],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeOut',
      },
    },
  };

  // Node appearance animation
  const nodeVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: (i) => ({
      scale: 1,
      opacity: 1,
      transition: {
        delay: i * 0.08,
        duration: 0.6,
        type: 'spring',
        stiffness: 150,
        damping: 15,
      },
    }),
  };

  // Glow pulse for edges
  const glowPulseVariants = {
    initial: { opacity: 0.4 },
    animate: {
      opacity: [0.4, 0.8, 0.4],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <div className="relative w-full h-full min-h-[500px]" data-testid="neural-network">
      <svg
        ref={svgRef}
        viewBox="0 0 700 600"
        className="w-full h-full"
      >
        <defs>
          {/* Input to Center gradient */}
          <linearGradient id="inputGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.9" />
          </linearGradient>
          
          {/* Center to Output gradient */}
          <linearGradient id="outputGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0.6" />
          </linearGradient>
          
          {/* Glow filters */}
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          
          <filter id="strongGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="8" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Input to Center connections */}
        {inputNodes.map((node, index) => (
          <motion.path
            key={`input-${node.id}`}
            d={generateCurvedPath(node, centralNode, 0.2)}
            stroke="url(#inputGradient)"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            variants={edgeVariants}
            initial="hidden"
            animate="visible"
            custom={index}
            filter="url(#softGlow)"
          />
        ))}

        {/* Center to Output connections */}
        {outputNodes.map((node, index) => (
          <motion.path
            key={`output-${node.id}`}
            d={generateCurvedPath(centralNode, node, 0.2)}
            stroke="url(#outputGradient)"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            variants={edgeVariants}
            initial="hidden"
            animate="visible"
            custom={index + inputNodes.length}
            filter="url(#softGlow)"
          />
        ))}

        {/* Animated glow lines - Input side */}
        {inputNodes.map((node, index) => (
          <motion.path
            key={`glow-input-${node.id}`}
            d={generateCurvedPath(node, centralNode, 0.2)}
            stroke="#8b5cf6"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            opacity="0"
            animate={{
              opacity: [0, 0.6, 0],
              pathLength: [0, 1, 1],
            }}
            transition={{
              duration: 2,
              delay: 2 + index * 0.3,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeInOut',
            }}
          />
        ))}

        {/* Animated glow lines - Output side */}
        {outputNodes.map((node, index) => (
          <motion.path
            key={`glow-output-${node.id}`}
            d={generateCurvedPath(centralNode, node, 0.2)}
            stroke="#a855f7"
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            opacity="0"
            animate={{
              opacity: [0, 0.6, 0],
              pathLength: [0, 1, 1],
            }}
            transition={{
              duration: 2,
              delay: 3 + index * 0.3,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeInOut',
            }}
          />
        ))}

        {/* Input nodes */}
        {inputNodes.map((node, index) => (
          <g key={`input-node-${node.id}`}>
            <motion.circle
              cx={node.x}
              cy={node.y}
              r="8"
              fill="#3b82f6"
              variants={nodeVariants}
              initial="hidden"
              animate="visible"
              custom={index}
            />
            <motion.circle
              cx={node.x}
              cy={node.y}
              r="4"
              fill="white"
              opacity="0.8"
              variants={nodeVariants}
              initial="hidden"
              animate="visible"
              custom={index}
            />
            {/* Text background for readability */}
            <motion.rect
              x={node.x - 85}
              y={node.y - 10}
              width="70"
              height="20"
              rx="4"
              fill="white"
              opacity="0.9"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.9 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
            />
            <motion.text
              x={node.x - 50}
              y={node.y + 4}
              textAnchor="middle"
              fill="#18181b"
              fontSize="11"
              fontFamily="Instrument Sans"
              fontWeight="600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
            >
              {node.label}
            </motion.text>
          </g>
        ))}

        {/* Central Doora node with pulse rings */}
        <motion.circle
          cx={centralNode.x}
          cy={centralNode.y}
          r="60"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="1"
          variants={centralPulseVariants}
          initial="initial"
          animate="animate"
        />
        <motion.circle
          cx={centralNode.x}
          cy={centralNode.y}
          r="45"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="1"
          variants={centralPulseVariants}
          initial="initial"
          animate="animate"
          style={{ animationDelay: '0.5s' }}
        />
        
        {/* Central node background */}
        <motion.circle
          cx={centralNode.x}
          cy={centralNode.y}
          r="35"
          fill="white"
          filter="url(#strongGlow)"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6, type: 'spring' }}
        />
        
        {/* Doora Logo */}
        <motion.image
          href="/assets/doora_logo.png"
          x={centralNode.x - 20}
          y={centralNode.y - 20}
          width="40"
          height="40"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 1, duration: 0.5, type: 'spring' }}
        />
        
        {/* Central label */}
        <motion.text
          x={centralNode.x}
          y={centralNode.y + 55}
          textAnchor="middle"
          fill="#71717a"
          fontSize="12"
          fontFamily="Instrument Sans"
          fontWeight="600"
          initial={{ opacity: 0, y: centralNode.y + 70 }}
          animate={{ opacity: 1, y: centralNode.y + 55 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          Doora
        </motion.text>

        {/* Output nodes */}
        {outputNodes.map((node, index) => (
          <g key={`output-node-${node.id}`}>
            <motion.circle
              cx={node.x}
              cy={node.y}
              r="8"
              fill="#a855f7"
              variants={nodeVariants}
              initial="hidden"
              animate="visible"
              custom={index + inputNodes.length}
            />
            <motion.circle
              cx={node.x}
              cy={node.y}
              r="4"
              fill="white"
              opacity="0.8"
              variants={nodeVariants}
              initial="hidden"
              animate="visible"
              custom={index + inputNodes.length}
            />
            {/* Text background for readability */}
            <motion.rect
              x={node.x + 15}
              y={node.y - 10}
              width="70"
              height="20"
              rx="4"
              fill="white"
              opacity="0.9"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.9 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
            />
            <motion.text
              x={node.x + 50}
              y={node.y + 4}
              textAnchor="middle"
              fill="#18181b"
              fontSize="11"
              fontFamily="Instrument Sans"
              fontWeight="600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
            >
              {node.label}
            </motion.text>
          </g>
        ))}

        {/* Section labels */}
        <motion.text
          x="130"
          y="40"
          textAnchor="middle"
          fill="#a1a1aa"
          fontSize="10"
          fontFamily="Instrument Sans"
          fontWeight="600"
          letterSpacing="0.1em"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          {lang.dataSourcesLabel}
        </motion.text>
        <motion.text
          x="570"
          y="40"
          textAnchor="middle"
          fill="#a1a1aa"
          fontSize="10"
          fontFamily="Instrument Sans"
          fontWeight="600"
          letterSpacing="0.1em"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          {lang.aiOutputsLabel}
        </motion.text>
      </svg>
    </div>
  );
};

export default NeuralNetwork;
